let ThreeDManager = function (___settings) {
  const THREE = require('../../../../externals/three'),
        GLOBAL_UTILS = require('../../../../shared/util/GlobalUtils'),
        ThreeDManagerInterface = require('../../../interfaces/ThreeDManagerInterface'),
        THREE_D_MANAGER_CONSTANTS = require('./ThreeDManagerConstants'),
        ExpoTHREE = ___settings.ExpoTHREE,
        _settings = ___settings.settings,
        _scene = ___settings.scene,
        _geometryNode = ___settings.geometryNode,
        _container = ___settings.container,
        _handlers = {};

  let that, _helpers,
      _groundPlane, _grid,
      _active = true;

  /**
   * Toggles the groundPlane visibility mode.
   *
   * @param {Boolean} toggle To what to set the mode
   * @returns {Boolean} If the mode was successfully set
   */
  let _groundPlaneVisibilityHook = function (toggle) {
    if (!GLOBAL_UTILS.typeCheck(toggle, 'boolean', that.warn, 'ThreeDManager.Hook->groundPlaneVisibility')) return false;

    if (_groundPlane) _groundPlane.visible = toggle;
    return true;
  };
  
  /**
   * Toggles the grid visibility mode.
   *
   * @param {Boolean} toggle To what to set the mode
   * @returns {Boolean} If the mode was successfully set
   */
  let _gridVisibilityHook = function (toggle) {
    if (!GLOBAL_UTILS.typeCheck(toggle, 'boolean', that.warn, 'ThreeDManager.Hook->gridVisibility')) return false;

    if (_grid) _grid.visible = toggle;
    return true;
  };

  class ThreeDManager extends ThreeDManagerInterface {

    constructor(___settings) {
      super(___settings, GLOBAL_UTILS);

      that = this;
      that.constants = THREE_D_MANAGER_CONSTANTS;
      _handlers.threeDManager = that;

      _helpers = new (require('../../../helpers/ThreeDManagerHelpers'))({
        scene: _scene,
        geometryNode: _geometryNode,
      }, _handlers);
      _handlers.threeDManager.helpers = _helpers;

      ////////////
      ////////////
      //
      // Settings
      //
      ////////////
      ////////////

      require('../../../../shared/mixins/SettingsMixin').call(that, _settings, THREE_D_MANAGER_CONSTANTS.defaultSettings, ___settings.runtimeId);

      ////////////
      ////////////
      //
      // Rendering Handler
      //
      ////////////
      ////////////

      this.renderingHandler = new (require('./handlers/RenderingHandler'))({
        settings: that.getSection('render'),
        scene: _scene,
        container: _container,
        externalLibraries: {
          ExpoTHREE: ExpoTHREE,
        }
      }, _handlers);
      _handlers.renderingHandler = this.renderingHandler;

      // error in the handler creation, abort
      if (that.success === false) return;

      ////////////
      ////////////
      //
      // Material Handler
      //
      ////////////
      ////////////

      this.materialHandler = new (require('./handlers/MaterialHandler'))({
        settings: that.getSection('material'),
        externalLibraries: {
          ExpoTHREE: ExpoTHREE,
        }
      }, _handlers);
      _handlers.materialHandler = this.materialHandler;

      // error in the handler creation, abort
      if (that.success === false) return;

      ////////////
      ////////////
      //
      // Camera Handler
      //
      ////////////
      ////////////

      this.cameraHandler = new (require('./handlers/CameraHandler'))({
        settings: that.getSection('camera'),
        container: _container,
        geometryNode: _geometryNode,
        externalLibraries: {
          ExpoTHREE: ExpoTHREE,
        }
      }, _handlers);
      _handlers.cameraHandler = this.cameraHandler;

      // error in the handler creation, abort
      if (that.success === false) return;

      ////////////
      ////////////
      //
      // Light Handler
      //
      ////////////
      ////////////

      this.lightHandler = new (require('./handlers/LightHandler'))({
        externalLibraries: {
          ExpoTHREE: ExpoTHREE,
        }
      }, _handlers);
      _handlers.lightHandler = this.lightHandler;
      
      // error in the handler creation, abort
      if (that.success === false) return;

      this.registerHook('groundPlaneVisibility', _groundPlaneVisibilityHook);
      this.registerHook('gridVisibility', _gridVisibilityHook);
    }

    ////////////
    ////////////
    //
    // ThreeDManager API
    //
    ////////////
    ////////////

    /** @inheritdoc */
    init(bb) {
      let sceneExtents = bb.min.distanceTo(bb.max);

      let divisions = 0.1;
      let gridExtents = sceneExtents;
      if (sceneExtents > 1) {
        let tmp = parseInt(sceneExtents);
        tmp = tmp.toString();
        tmp = tmp.length;
        tmp = Math.pow(10, tmp - 1);
        gridExtents = Math.max(Math.ceil(sceneExtents / tmp) * tmp, 1);
        tmp = tmp / 10;
        divisions = gridExtents / tmp;
      }
      else {
        let zeros = 1 - Math.floor(Math.log(sceneExtents) / Math.log(10)) - 2;
        let r = sceneExtents.toFixed(zeros + 1);
        let firstDigit = parseInt(r.substr(r.length - 1)) + 1;
        gridExtents = '0.';
        for (let i = 0; i < zeros; ++i)
          gridExtents = gridExtents + '0';
        gridExtents = parseFloat(gridExtents + firstDigit);
        divisions = firstDigit * 10;
      }

      _grid = new THREE.GridHelper(2 * gridExtents, divisions);
      _grid.material.opacity = 0.15;
      _grid.material.transparent = true;
      _grid.isSelectable = false;
      _grid.rotateX(Math.PI / 2);
      _grid.visible = that.getSetting('gridVisibility');
      _grid.position.set(0, 0, bb.min.z - 0.005);
      _helpers.addViewportObject( _grid );

      let groundPlaneGeom = new THREE.PlaneGeometry(2 * gridExtents, 2 * gridExtents, 2, 2);
      let groundPlaneMaterialProperties = { color: 0xD3D3D3, side: THREE.FrontSide, roughness: 1.0, metalness: 0.0 };
      _groundPlane = new THREE.Mesh(groundPlaneGeom, _handlers.materialHandler.getMaterial(groundPlaneMaterialProperties));
      _groundPlane.receiveShadow = true;
      _groundPlane.visible = that.getSetting('groundPlaneVisibility');
      _groundPlane.position.set(0, 0, bb.min.z - 0.005);
      _helpers.addViewportObject( _groundPlane );

      _helpers.initialize();
      that.adjustScene();

      _handlers.renderingHandler.render();
    }

    /** @inheritdoc */
    adjustScene() {
      if(!_helpers.isInitialized()) return;
      
      let bb = _geometryNode.computeSceneBoundingBox();
      let bs = new THREE.Sphere();
      bb.getBoundingSphere(bs);

      _grid.position.set(0, 0, bb.min.z - 0.005);
      _groundPlane.position.set(0, 0, bb.min.z - 0.005);

      _handlers.lightHandler.adjustToBoundingSphere(bs);
      _handlers.cameraHandler.adjustToBoundingSphere(bs);
      _handlers.renderingHandler.updateShadowMap();
      _handlers.materialHandler.compile();
    }

    /** @inheritdoc */
    computeBoundingBox(scenePaths) {
      return _geometryNode.computeBoundingBox(scenePaths);
    }




    /** @inheritdoc */
    pause() {
      _active = false;
      _handlers.renderingHandler.pause();
      _handlers.cameraHandler.pause();
      _handlers.lightHandler.pause();
      _handlers.materialHandler.pause();
    }
    
    /** @inheritdoc */
    resume() {
      _active = true;
      _handlers.renderingHandler.resume();
      _handlers.cameraHandler.resume();
      _handlers.lightHandler.resume();
      _handlers.materialHandler.resume();
    }

    /** @inheritdoc */
    destroy() {
      _helpers.destroyViewport();

      // call handler destroy methods
      if(_handlers.renderingHandler)
        _handlers.renderingHandler.destroy();

      if(_handlers.cameraHandler)
        _handlers.cameraHandler.destroy();

      if(_handlers.lightHandler)
        _handlers.lightHandler.destroy();

      if(_handlers.materialHandler)
        _handlers.materialHandler.destroy();
        
      // de-register hooks and notifiers
      that.deregisterAllHooks();
      that.deregisterAllNotifiers();

      //while (that.container.firstChild)
      //  that.container.removeChild(that.container.firstChild);

      // remove handlers
      for (let key in _handlers)
        delete _handlers[key];

      for (let key in that)
        that[key] = undefined;
    }





    /** @inheritdoc */
    addMesh(mesh, material, properties) {
      _helpers.addMesh(mesh, material, properties);
    }

    /** @inheritdoc */
    removeMesh(mesh) {
      _helpers.removeMesh(mesh);
    }

    /** @inheritdoc */
    addAnchor(object, properties) {
      that.debug('This method is not implemented in this viewport.');
    }

    /** @inheritdoc */
    removeAnchor(object) {
      that.debug('This method is not implemented in this viewport.');
    }




    /** @inheritdoc */
    fadeIn(path, duration) {
      that.debug('This method is not implemented in this viewport.');
    }

    /** @inheritdoc */
    fadeOut(path, duration) {
      that.debug('This method is not implemented in this viewport.');
    }




    /** @inheritdoc */
    setLiveTransformation(group, scenePaths, transformations, reset, duration) {
      that.debug('This method is not implemented in this viewport.');
    }




    /** @inheritdoc */
    updateInteractions(path, options) {
      that.debug('This method is not implemented in this viewport.');
    }

    /** @inheritdoc */
    removeFromInteractions(path) {
      that.debug('This method is not implemented in this viewport.');
    }
  }

  return new ThreeDManager(___settings);
};


module.exports = ThreeDManager;
